import { useFlags } from 'launchdarkly-react-client-sdk';
import { createContext, useContext, useMemo } from 'react';

/**
 * Which tier enforces each flag. A `ui-only` flag hides UI and changes no API
 * behavior — never treat one as a kill switch during an incident (AMP-616).
 */
export type FeatureFlagTier =
  /** Hides UI. The API serves the feature to any authorized caller regardless. */
  | 'ui-only'
  /** The same key is evaluated server-side, so turning it off stops the API. */
  | 'ui-and-api';

export interface FeatureFlags {
  aiChat: boolean;
  chatContract: boolean;
  clientPortalSpending: boolean;
  disputeManagement: boolean;
  knowledgeCapture: boolean;
  liftAiInsights: boolean;
  liftaiAiInvoiceReview: boolean;
  listQuickPreview: boolean;
  reportsRelaunch: boolean;
  tasks: boolean;
  workQueue: boolean;
}

// `ui-and-api` flags are evaluated server-side under the same key; see the
// table in services/api/AGENTS.md for which endpoint each one gates.
export const FEATURE_FLAG_TIERS: Record<keyof FeatureFlags, FeatureFlagTier> = {
  aiChat: 'ui-and-api',
  chatContract: 'ui-and-api',
  clientPortalSpending: 'ui-and-api',
  disputeManagement: 'ui-and-api',
  knowledgeCapture: 'ui-and-api',
  liftAiInsights: 'ui-and-api',
  liftaiAiInvoiceReview: 'ui-only',
  listQuickPreview: 'ui-only',
  reportsRelaunch: 'ui-only',
  tasks: 'ui-and-api',
  workQueue: 'ui-and-api',
};

const FLAG_DEFAULTS: FeatureFlags = {
  aiChat: false,
  chatContract: false,
  clientPortalSpending: false,
  disputeManagement: false,
  knowledgeCapture: false,
  liftAiInsights: false,
  liftaiAiInvoiceReview: false,
  listQuickPreview: false,
  reportsRelaunch: false,
  tasks: false,
  workQueue: false,
};

/**
 * Set by the app shell's LaunchDarkly provider. `false` only while the client is still
 * initializing; it turns `true` once flags are delivered, initialization fails, or the
 * shell stops waiting, and never goes back.
 *
 * Defaults to `true` so that anything rendered without the provider (screenshot mode,
 * which sets no client ID, and component tests) has nothing to wait for.
 */
export const FlagsReadyContext = createContext(true);

/**
 * Tells "flag off" apart from "flag not delivered yet", which {@link useFeatureFlags}
 * cannot: both read `false` there. Gate on this before acting once on a flag's value,
 * such as consuming a deep link that names flag-gated UI.
 *
 * `true` does not promise LaunchDarkly answered. After a failure or timeout every flag
 * reads `false`, and a late delivery can still turn one on afterwards.
 */
export function useFlagsReady(): boolean {
  return useContext(FlagsReadyContext);
}

/**
 * Typed `useFlags()`. Only an exact `true` enables a flag: anything LaunchDarkly
 * has not delivered yet, or a value retyped to a non-boolean, reads as `false`.
 * Matches `liftai.flags`, which rejects non-boolean values server-side. Pair it with
 * {@link useFlagsReady} to tell those two apart.
 */
export function useFeatureFlags(): FeatureFlags {
  const flags = useFlags();

  return useMemo(() => {
    const resolved = { ...FLAG_DEFAULTS };
    for (const key of Object.keys(FLAG_DEFAULTS) as (keyof FeatureFlags)[]) {
      resolved[key] = flags[key] === true;
    }
    return resolved;
  }, [flags]);
}
